import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Calendar, Users, BarChart2, Wifi, Bell, Route,
  ChevronDown, ChevronRight, Menu, X, Play, Star,
  CheckCircle, ArrowRight, Smartphone, Package, Clock,
  Shield, Zap, Navigation, FileText, Volume2, VolumeX
} from "lucide-react";

const SCREENSHOT_DASHBOARD = "https://customer-assets.emergentagent.com/job_sales-visit-tracker-9/artifacts/csfegpsx_Gemini_Generated_Image_x6sffyx6sffyx6sf.png";
const SCREENSHOT_NAV = "https://static.prod-images.emergentagent.com/jobs/e71c7936-71a6-4f8e-9d07-040669a291d1/images/71aa8db4db5fb7c207f75772204f04598cabc2a8d2aba7f52c690b7e89c40fd0.jpeg";

const FEATURES = [
  { icon: Navigation, title: "Navigazione Step-by-Step", desc: "Nessun limite di tappe: premi Naviga, arrivi, premi Prossima. Google Maps si apre su un'unica destinazione alla volta, per 15+ visite al giorno senza problemi.", hot: true },
  { icon: Route, title: "Giro Ottimizzato", desc: "L'algoritmo calcola il percorso più efficiente partendo dalla tua posizione GPS. Riordina con trascinamento se vuoi." },
  { icon: Calendar, title: "Calendario Automatico", desc: "Pianifica le visite con frequenza personalizzata. L'app costruisce e aggiorna il calendario da sola." },
  { icon: Users, title: "Gestione Clienti", desc: "Anagrafica completa, storico ordini, note e data ultimo ordine sempre a portata di mano." },
  { icon: BarChart2, title: "Statistiche & Report", desc: "Tasso di conversione, clienti più redditizi, andamento mensile. Tutto in un'unica schermata." },
  { icon: Wifi, title: "Funziona Offline", desc: "Nessuna copertura? L'app funziona lo stesso. I dati si sincronizzano appena torni online." },
  { icon: Bell, title: "Promemoria Push", desc: "Notifiche automatiche sui clienti da visitare oggi. Non devi ricordarti di nulla." },
  { icon: FileText, title: "Import Excel", desc: "Carica tutta la lista clienti in un click da un file Excel. Migrazione in pochi secondi." },
];

const STEPS = [
  { num: "01", title: "Importa i tuoi clienti", desc: "Carica la lista clienti da Excel o aggiungili manualmente. In pochi minuti hai tutto in ordine.", icon: Users },
  { num: "02", title: "Pianifica le visite", desc: "Imposta la frequenza di visita per ogni cliente. L'app costruisce automaticamente il tuo calendario.", icon: Calendar },
  { num: "03", title: "Percorso ottimizzato", desc: "La mappa ti mostra il giro più efficiente. Avvia la navigazione e visita più clienti in meno tempo.", icon: MapPin },
  { num: "04", title: "Registra esiti e ordini", desc: "Dopo ogni visita, registra l'esito in 2 tap. Lo storico si aggiorna automaticamente.", icon: CheckCircle },
];

const PRICING = [
  {
    name: "Free", price: "0", period: "per sempre",
    desc: "Per iniziare subito, senza rischi",
    features: ["Fino a 30 clienti", "Calendario visite base", "Gestione appuntamenti", "App mobile PWA", "Funziona offline"],
    cta: "Inizia Gratis", highlight: false,
  },
  {
    name: "Premium", price: "4,90", period: "al mese",
    desc: "Per agenti seri che vogliono crescere",
    features: ["Clienti illimitati", "Giro visite su mappa GPS", "Auto-riprogrammazione visite", "Statistiche avanzate", "Promemoria push", "Import Excel", "Supporto prioritario"],
    cta: "Prova 14 giorni gratis", highlight: true, badge: "Più popolare",
  },
  {
    name: "Business", price: "Su misura", period: "",
    desc: "Per team e reti vendita",
    features: ["Tutto di Premium", "Gestione team agenti", "Dashboard manager", "Report consolidati", "Biglietti da visita digitali", "Onboarding dedicato", "Prezzo personalizzato"],
    cta: "Contattaci", highlight: false,
  },
];

const FAQS = [
  { q: "È davvero gratuita?", a: "Sì, il piano Free è gratuito per sempre senza carta di credito. Puoi gestire fino a 30 clienti con tutte le funzioni base." },
  { q: "Funziona su iPhone e Android?", a: "venditoreElite è una PWA (Progressive Web App). Si installa direttamente dal browser su qualsiasi dispositivo — iPhone, Android, tablet — senza passare dall'App Store." },
  { q: "Come si installa sul telefono?", a: "Apri venditoreelite.it da Safari (iPhone) o Chrome (Android), clicca 'Aggiungi a schermata Home' e l'app appare come un'app normale. Nessun App Store necessario." },
  { q: "I dati sono al sicuro?", a: "Tutti i dati sono salvati su server europei con crittografia SSL. Non condividiamo mai i tuoi dati con terzi." },
  { q: "Posso importare i miei clienti da Excel?", a: "Sì! Con il piano Premium puoi caricare un file Excel con tutti i tuoi clienti in un click. La migrazione richiede pochi secondi." },
  { q: "Cosa succede se non ho internet?", a: "L'app funziona completamente offline. Puoi visualizzare clienti, registrare visite e inserire ordini. I dati vengono sincronizzati automaticamente appena torni online." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const heroVideoRef = useRef(null);

  function toggleMute() {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = !heroVideoRef.current.muted;
      setVideoMuted(heroVideoRef.current.muted);
    }
  }
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setShowInstall(true); };
    window.addEventListener("beforeinstallprompt", handler);
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => { window.removeEventListener("beforeinstallprompt", handler); window.removeEventListener("scroll", onScroll); };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null); setShowInstall(false);
  };

  const goToAuth = () => navigate("/auth");

  return (
    <div className="min-h-screen bg-[#0F172A] text-white overflow-x-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-[#0F172A]/90 border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            venditor<span className="text-[#2563EB]">Elite</span>
          </span>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/60">
            <a href="#come-funziona" className="hover:text-white transition">Come funziona</a>
            <a href="#funzionalita" className="hover:text-white transition">Funzionalità</a>
            <a href="#prezzi" className="hover:text-white transition">Prezzi</a>
            <a href="#faq" className="hover:text-white transition">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={goToAuth} data-testid="header-login-btn"
              className="hidden md:block text-sm text-white/70 hover:text-white transition px-4 py-2">
              Accedi
            </button>
            <button onClick={goToAuth} data-testid="header-cta-btn"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-bold px-5 py-2 rounded-full transition">
              Prova Gratis
            </button>
            <button className="md:hidden text-white/70" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-[#0F172A] border-t border-white/10 px-4 py-4 flex flex-col gap-4 text-sm">
            <a href="#come-funziona" onClick={() => setMenuOpen(false)} className="text-white/70 hover:text-white">Come funziona</a>
            <a href="#funzionalita" onClick={() => setMenuOpen(false)} className="text-white/70 hover:text-white">Funzionalità</a>
            <a href="#prezzi" onClick={() => setMenuOpen(false)} className="text-white/70 hover:text-white">Prezzi</a>
            <a href="#faq" onClick={() => setMenuOpen(false)} className="text-white/70 hover:text-white">FAQ</a>
            <button onClick={goToAuth} className="bg-[#2563EB] text-white font-bold py-2 rounded-full" data-testid="mobile-cta-btn">Prova Gratis</button>
          </div>
        )}
      </header>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden bg-[#020617]">
        {/* Fluid gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-100px] left-[10%] w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-50px] right-[5%] w-[500px] h-[500px] bg-[#38BDF8]/8 rounded-full blur-[100px]" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#020617]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">

          {/* Sinistra — testo */}
          <div>
            <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-4 py-1.5 text-xs text-[#38BDF8] font-medium mb-8">
              <Zap size={12} /> Gratuito · Nessuna carta richiesta · Si installa in 30 secondi
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-none tracking-tight mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              L&apos;app che gestisce<br />
              <span className="text-[#2563EB]">le tue visite</span><br />
              per te.
            </h1>
            <p className="text-white/60 text-lg mb-8 max-w-lg leading-relaxed">
              Calendario automatico, giro clienti su mappa GPS, storico ordini e promemoria push.
              Tutto offline. Tutto gratis.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <button onClick={goToAuth} data-testid="hero-cta-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-8 py-4 rounded-full text-base transition flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25">
                Inizia Gratis <ArrowRight size={18} />
              </button>
            </div>
            <div className="flex items-center gap-6 pt-8 border-t border-white/10">
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-[#020617] flex items-center justify-center text-xs font-bold">
                    {["M","L","G","R"][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex gap-0.5 mb-0.5">
                  {[...Array(5)].map((_, i) => <Star key={i} size={12} className="text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-xs text-white/50">Usato da agenti in tutta Italia</p>
              </div>
            </div>
            {showInstall && (
              <button onClick={handleInstall} data-testid="pwa-install-btn"
                className="mt-4 flex items-center gap-2 text-sm text-white/50 hover:text-white/80 transition">
                <Smartphone size={14} /> Installa sul telefono
              </button>
            )}
          </div>

          {/* Destra — video in cornice browser */}
          <div className="relative flex items-center justify-center">
            <div className="absolute w-[400px] h-[400px] bg-[#2563EB]/10 rounded-full blur-[80px]" />
            <div className="relative z-10 w-full max-w-[420px]">
              <div className="bg-[#0F172A] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
                <div className="bg-[#1E293B] px-4 py-2 flex items-center gap-2 border-b border-white/10">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-[#FF5F57]" />
                    <div className="w-2 h-2 rounded-full bg-[#FFBD2E]" />
                    <div className="w-2 h-2 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 bg-[#0F172A]/60 rounded-md px-3 py-0.5 text-[10px] text-white/25 text-center">
                    venditoreelite.it/app
                  </div>
                </div>
                <div className="relative">
                  <video
                    ref={heroVideoRef}
                    src="https://customer-assets-lxgj4vgw.emergentagent.net/job_sales-visit-tracker-9/artifacts/63747688844ed095_venditoreElite_%20come%20funziona%20v1.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full block"
                  />
                  <button onClick={toggleMute} data-testid="video-mute-btn"
                    className="absolute bottom-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors backdrop-blur-sm"
                    title={videoMuted ? "Attiva audio" : "Disattiva audio"}>
                    {videoMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown size={20} className="text-white/30" />
        </div>
      </section>

      {/* ── NAVIGAZIONE STEP-BY-STEP SPOTLIGHT ─────────────────────── */}
      <section className="bg-[#020617] py-20 px-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* Left: text */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 text-xs text-emerald-400 font-bold uppercase tracking-wider mb-6">
              <Zap size={11} /> Nuova funzione
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight mb-5"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Fino a 15 visite al giorno.<br />
              <span className="text-[#2563EB]">Google Maps tappa per tappa.</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8 max-w-lg">
              Google Maps limita i percorsi a 9 tappe. venditoreElite non ha questo problema: ottimizza il giro, poi ti guida <strong className="text-white">una destinazione alla volta</strong>. Arrivi, premi "Prossima tappa", vai avanti. Semplice come deve essere.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { icon: Play, label: "Premi \"Avvia navigazione\"", desc: "Google Maps si apre sulla prima tappa" },
                { icon: Navigation, label: "Arrivi dal cliente", desc: "Registra l'esito o passa direttamente avanti" },
                { icon: ArrowRight, label: "Premi \"Prossima tappa\"", desc: "La tappa successiva si apre automaticamente" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-xl bg-[#2563EB]/20 border border-[#2563EB]/30 flex items-center justify-center shrink-0 mt-0.5">
                    <item.icon size={14} className="text-[#38BDF8]" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{item.label}</div>
                    <div className="text-xs text-white/50">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={goToAuth} data-testid="nav-feature-cta-btn"
              className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-7 py-3.5 rounded-full text-sm transition shadow-lg shadow-blue-500/20">
              Provala adesso <ArrowRight size={16} />
            </button>
          </div>

          {/* Right: phone mockup */}
          <div className="order-1 lg:order-2 flex justify-center">
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-[#2563EB]/20 blur-[60px] rounded-full scale-75" />
              {/* Phone frame */}
              <div className="relative z-10 bg-[#0F172A] rounded-[2.5rem] border-[3px] border-white/10 shadow-2xl overflow-hidden w-[240px] sm:w-[270px]">
                <div className="bg-[#1E293B] h-7 flex items-center justify-center">
                  <div className="w-16 h-3 bg-[#0F172A] rounded-full" />
                </div>
                <img
                  src={SCREENSHOT_NAV}
                  alt="Navigazione step-by-step venditoreElite"
                  className="w-full block"
                  style={{ maxHeight: "480px", objectFit: "cover", objectPosition: "top" }}
                />
              </div>
              {/* Badge floating */}
              <div className="absolute -right-6 top-16 bg-[#2563EB] rounded-xl px-3 py-2 shadow-xl z-20">
                <div className="text-[10px] text-white/70 font-medium">Nessun limite</div>
                <div className="text-sm font-extrabold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>15+ tappe/giorno</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── COME FUNZIONA ────────────────────────────────────────────── */}
      <section id="come-funziona" className="bg-[#0F172A] py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">Come funziona</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Da zero a produttivo<br />in 4 passi
            </h2>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Step 1 - Large */}
            <div className="md:col-span-7 bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/[0.07] transition-colors">
              <div className="text-6xl font-black text-[#2563EB]/20 mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>01</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Importa i tuoi clienti</h3>
              </div>
              <p className="text-white/50 text-sm leading-relaxed mb-6">Carica la lista clienti da un file Excel o aggiungili manualmente uno per uno. In pochi minuti hai tutta la tua rete clienti organizzata e pronta.</p>
              <div className="flex items-center gap-2 text-xs text-[#38BDF8]">
                <CheckCircle size={14} /> Supporta file .xlsx con migliaia di clienti
              </div>
            </div>

            {/* Step 2 - Small */}
            <div className="md:col-span-5 bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/[0.07] transition-colors">
              <div className="text-6xl font-black text-[#2563EB]/20 mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>02</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
                  <Calendar size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Calendario automatico</h3>
              </div>
              <p className="text-white/50 text-sm leading-relaxed">Imposta la frequenza di visita (ogni 2 settimane, ogni mese...) e l&apos;app costruisce il calendario per te. Zero pianificazione manuale.</p>
            </div>

            {/* Screenshot Dashboard */}
            <div className="md:col-span-5 bg-white/5 border border-white/10 rounded-3xl overflow-hidden hover:bg-white/[0.07] transition-colors">
              <div className="overflow-hidden" style={{ maxHeight: "220px" }}>
                <img src={SCREENSHOT_DASHBOARD} alt="Dashboard giornaliera venditoreElite"
                  className="w-full object-cover object-top block" style={{ maxHeight: "220px" }} />
              </div>
              <div className="p-5">
                <div className="text-xs font-bold text-[#38BDF8] uppercase tracking-wider mb-1">Dashboard</div>
                <p className="text-sm text-white/60">Visite di oggi, clienti scaduti e KPI giornalieri in un colpo d&apos;occhio</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="md:col-span-7 bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/[0.07] transition-colors">
              <div className="text-6xl font-black text-[#2563EB]/20 mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>03</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
                  <MapPin size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Percorso ottimizzato su mappa</h3>
              </div>
              <p className="text-white/50 text-sm leading-relaxed mb-6">La mappa mostra il giro più efficiente per la giornata. Premi <strong className="text-white">"Avvia navigazione"</strong>: Google Maps si apre tappa per tappa — nessun limite di destinazioni, anche con 15 visite al giorno.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-xl p-3 text-xs text-white/60">
                  <Navigation size={14} className="text-[#38BDF8] mb-1" />
                  Navigazione GPS integrata
                </div>
                <div className="bg-white/5 rounded-xl p-3 text-xs text-white/60">
                  <Route size={14} className="text-[#38BDF8] mb-1" />
                  Riordina con drag & drop
                </div>
              </div>
            </div>

            {/* Screenshot Navigazione (nuova funzione) */}
            <div className="md:col-span-7 bg-white/5 border border-[#2563EB]/30 rounded-3xl overflow-hidden hover:bg-white/[0.07] transition-colors relative">
              <div className="absolute top-3 right-3 z-10 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">Novità</div>
              <div className="overflow-hidden flex justify-center bg-[#0A1628] py-4" style={{ maxHeight: "260px" }}>
                <img src={SCREENSHOT_NAV} alt="Navigazione step-by-step venditoreElite"
                  className="h-full object-cover object-top block rounded-xl shadow-xl"
                  style={{ maxHeight: "240px", width: "auto" }} />
              </div>
              <div className="p-5">
                <div className="text-xs font-bold text-[#38BDF8] uppercase tracking-wider mb-1">Navigazione Step-by-Step</div>
                <p className="text-sm text-white/60">Una tappa alla volta su Google Maps — nessun limite di destinazioni</p>
              </div>
            </div>

            {/* Step 4 */}
            <div className="md:col-span-5 bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/[0.07] transition-colors">
              <div className="text-6xl font-black text-[#2563EB]/20 mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>04</div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#2563EB]/20 rounded-xl flex items-center justify-center">
                  <CheckCircle size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Registra esito in 2 tap</h3>
              </div>
              <p className="text-white/50 text-sm leading-relaxed">Dopo ogni visita, registra ordine, appuntamento saltato o callback. Lo storico si aggiorna e la prossima visita si pianifica da sola.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FUNZIONALITÀ ─────────────────────────────────────────────── */}
      <section id="funzionalita" className="bg-[#020617] py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">Funzionalità</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Tutto quello che serve<br />a un agente moderno
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map((f, i) => (
              <div key={i} className={`bg-white/5 border rounded-2xl p-6 hover:-translate-y-1 transition-all duration-200 group relative ${f.hot ? "border-[#2563EB]/50 shadow-lg shadow-blue-500/10" : "border-white/10 hover:border-[#2563EB]/50"}`}>
                {f.hot && (
                  <div className="absolute -top-3 left-5 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide">Novità</div>
                )}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 transition-colors ${f.hot ? "bg-[#2563EB]/30 group-hover:bg-[#2563EB]/40" : "bg-[#2563EB]/20 group-hover:bg-[#2563EB]/30"}`}>
                  <f.icon size={20} className="text-[#38BDF8]" />
                </div>
                <h3 className="font-bold text-sm mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1rem" }}>{f.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────────────── */}
      <section className="bg-[#0F172A] py-16 px-4 border-y border-white/10">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { val: "100%", label: "Gratuito per sempre" },
            { val: "30s", label: "Installazione sul telefono" },
            { val: "Offline", label: "Funziona senza internet" },
            { val: "iOS+Android", label: "Compatibile con tutti" },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl font-extrabold text-[#2563EB] mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{s.val}</div>
              <div className="text-xs text-white/50">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── INSTALLAZIONE ────────────────────────────────────────────── */}
      <section className="bg-[#0F172A] py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">Installazione</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Si installa in 30 secondi
            </h2>
            <p className="text-white/50 mt-3 text-sm">Nessun App Store. Funziona come un&apos;app nativa su iPhone e Android.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* iOS */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                  <Smartphone size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-bold text-sm">iPhone / iPad</div>
                  <div className="text-xs text-white/40">Safari browser</div>
                </div>
              </div>
              <ol className="space-y-4">
                {[
                  { step: "1", text: "Apri Safari e vai su", highlight: "venditoreelite.it" },
                  { step: "2", text: "Tocca l'icona", highlight: "Condividi (□↑)" , sub: "in basso al centro" },
                  { step: "3", text: "Scorri e tocca", highlight: "\"Aggiungi a schermata Home\"" },
                  { step: "4", text: "Tocca", highlight: "\"Aggiungi\"", sub: "in alto a destra — fatto!" },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#2563EB]/20 border border-[#2563EB]/40 text-[#38BDF8] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {item.step}
                    </span>
                    <span className="text-sm text-white/60">
                      {item.text} <span className="text-white font-medium">{item.highlight}</span>
                      {item.sub && <span className="text-white/40"> — {item.sub}</span>}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 p-3 bg-[#2563EB]/10 rounded-xl border border-[#2563EB]/20">
                <p className="text-xs text-[#38BDF8]">Funziona offline · Notifiche push · Icona sulla home</p>
              </div>
            </div>

            {/* Android */}
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                  <Smartphone size={20} className="text-[#10B981]" />
                </div>
                <div>
                  <div className="font-bold text-sm">Android</div>
                  <div className="text-xs text-white/40">Chrome browser</div>
                </div>
              </div>
              <ol className="space-y-4">
                {[
                  { step: "1", text: "Apri Chrome e vai su", highlight: "venditoreelite.it" },
                  { step: "2", text: "Tocca i", highlight: "3 puntini (⋮)", sub: "in alto a destra" },
                  { step: "3", text: "Tocca", highlight: "\"Aggiungi a schermata Home\"" },
                  { step: "4", text: "Tocca", highlight: "\"Aggiungi\"", sub: "— l'icona appare subito!" },
                ].map((item) => (
                  <li key={item.step} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {item.step}
                    </span>
                    <span className="text-sm text-white/60">
                      {item.text} <span className="text-white font-medium">{item.highlight}</span>
                      {item.sub && <span className="text-white/40"> — {item.sub}</span>}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 p-3 bg-[#10B981]/10 rounded-xl border border-[#10B981]/20">
                <p className="text-xs text-[#10B981]">Funziona offline · Notifiche push · Icona sulla home</p>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-white/30">
              Alternativa: su alcuni dispositivi Android appare in automatico il banner<br/>
              <span className="text-white/50">&quot;Aggiungi venditoreElite alla schermata home&quot;</span> — basta toccarlo.
            </p>
          </div>
        </div>
      </section>

      {/* ── PREZZI ───────────────────────────────────────────────────── */}
      <section id="prezzi" className="bg-[#0F172A] py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">Prezzi</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Scegli il piano giusto per te
            </h2>
            <p className="text-white/50 mt-3">Inizia gratis, aggiorna quando vuoi. Nessun vincolo.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {PRICING.map((plan, i) => (
              <div key={i} className={`relative rounded-3xl p-8 border transition-all ${plan.highlight
                ? "bg-[#2563EB]/10 border-[#2563EB] scale-105 shadow-2xl shadow-blue-500/20"
                : "bg-white/5 border-white/10"}`}>
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#2563EB] text-white text-xs font-bold px-4 py-1 rounded-full">
                    {plan.badge}
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-xl font-extrabold mb-1" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>{plan.name}</h3>
                  <p className="text-white/50 text-xs mb-4">{plan.desc}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {plan.price === "Su misura" ? plan.price : `€${plan.price}`}
                    </span>
                    {plan.period && <span className="text-white/40 text-sm">/{plan.period}</span>}
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-white/70">
                      <CheckCircle size={14} className="text-[#10B981] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToAuth}
                  data-testid={`pricing-cta-${i}`}
                  className={`w-full py-3 rounded-full font-bold text-sm transition ${plan.highlight
                    ? "bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-lg shadow-blue-500/30"
                    : "border border-white/20 hover:bg-white/10 text-white"}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section id="faq" className="bg-[#020617] py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2563EB]">FAQ</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold mt-2 tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Domande frequenti
            </h2>
          </div>
          <div className="divide-y divide-white/10">
            {FAQS.map((faq, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <button
                  className="w-full flex items-center justify-between py-5 text-left hover:text-[#38BDF8] transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="font-semibold text-sm pr-4">{faq.q}</span>
                  <ChevronDown size={16} className={`shrink-0 text-white/40 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="pb-5 text-sm text-white/50 leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────── */}
      <section className="bg-[#0F172A] py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative bg-gradient-to-br from-[#2563EB]/20 to-[#0F172A] border border-[#2563EB]/30 rounded-3xl p-12 overflow-hidden">
            <div className="absolute inset-0 opacity-5" style={{
              backgroundImage: "radial-gradient(circle, #2563EB 1px, transparent 1px)",
              backgroundSize: "30px 30px"
            }} />
            <div className="relative">
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />)}
              </div>
              <h2 className="text-4xl sm:text-6xl font-extrabold mb-4 tracking-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Inizia oggi.<br />
                <span className="text-[#2563EB]">È completamente gratis.</span>
              </h2>
              <p className="text-white/50 mb-8 text-lg">Nessuna carta di credito. Nessun impegno. Si installa in 30 secondi.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={goToAuth} data-testid="final-cta-btn"
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold px-10 py-4 rounded-full text-lg transition flex items-center justify-center gap-2 shadow-xl shadow-blue-500/30">
                  Crea Account Gratis <ArrowRight size={20} />
                </button>
                {showInstall && (
                  <button onClick={handleInstall} data-testid="final-install-btn"
                    className="border border-white/20 hover:bg-white/5 text-white font-semibold px-10 py-4 rounded-full text-lg transition flex items-center justify-center gap-2">
                    <Smartphone size={20} /> Installa l&apos;App
                  </button>
                )}
              </div>
              <div className="flex items-center justify-center gap-6 mt-8 text-xs text-white/30">
                <span className="flex items-center gap-1"><Shield size={12} /> Dati al sicuro</span>
                <span className="flex items-center gap-1"><Wifi size={12} /> Funziona offline</span>
                <span className="flex items-center gap-1"><Smartphone size={12} /> iOS & Android</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────── */}
      <footer className="bg-[#020617] border-t border-white/10 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="text-2xl font-black mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                venditor<span className="text-[#2563EB]">Elite</span>
              </div>
              <p className="text-white/40 text-sm leading-relaxed max-w-xs">
                L&apos;app per agenti di commercio che vogliono organizzare meglio le visite e aumentare le vendite.
              </p>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white/30 mb-4">App</div>
              <div className="space-y-2 text-sm text-white/50">
                <a href="#funzionalita" className="block hover:text-white transition">Funzionalità</a>
                <a href="#prezzi" className="block hover:text-white transition">Prezzi</a>
                <a href="#faq" className="block hover:text-white transition">FAQ</a>
                <button onClick={goToAuth} className="block hover:text-white transition text-left">Accedi</button>
              </div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-white/30 mb-4">Legale</div>
              <div className="space-y-2 text-sm text-white/50">
                <a href="/privacy-policy" className="block hover:text-white transition">Privacy Policy</a>
                <a href="/cookie-policy" className="block hover:text-white transition">Cookie Policy</a>
                <a href="/contatti" className="block hover:text-white transition">Contatti</a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/30">
            <span>&copy; 2026 venditoreElite. Tutti i diritti riservati.</span>
            <span>Made in Italy · P.IVA in corso di registrazione</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
